import React, { useMemo, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Chip, Loading, ErrorState, ConfirmButton } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtMoney, fmtDate, todayStr } from "../lib/format.js";
import { useBooksData, entryCrud } from "./api.js";
import { EntryForm, EmployeeForm, PayrollForm, LoanForm } from "./forms.jsx";

const SUBS = [
  ["overview", "Overview"], ["income", "Income"], ["expenses", "Expenses"],
  ["payroll", "Payroll"], ["loans", "Loans"],
];

const Row = ({ label, amount, sub, chip }) => (
  <tr>
    <td>
      <div className="main-cell">{label}</div>
      {sub && <div className="sub-cell">{sub}</div>}
    </td>
    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(amount)}</td>
    <td style={{ width: 90 }}>{chip}</td>
  </tr>
);

export default function BooksTab({ onGoToTab }) {
  const [sub, setSub] = useState("overview");
  const [month, setMonth] = useState("all");
  const [entryModal, setEntryModal] = useState(null);
  const [empModal, setEmpModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [loanModal, setLoanModal] = useState(null);

  const books = useBooksData();
  const { notify } = useToast();
  const delEntry = entryCrud.useDelete();

  const inMonth = (dstr) => month === "all" || (dstr || "").slice(0, 7) === month;

  const d = useMemo(() => {
    if (books.isPending || books.error) return null;
    const rentals = books.rentals.data;
    const maintenance = books.maintenance.data;
    const entries = books.entries.data;
    const payroll = books.payroll.data;

    // Income is derived from paid rentals; only adjustments are stored.
    const paidRentals = rentals.filter(
      (r) => r.status !== "cancelled" && r.paid && inMonth(r.paid_date || r.start_date)
    );
    const sum = (rows, key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);

    const rentalIncome = sum(paidRentals, "rental_charge");
    const deliveryIncome = sum(paidRentals, "delivery_fee");
    const waiverIncome = sum(paidRentals, "damage_waiver");
    const lateFeeIncome = sum(paidRentals, "late_charge");
    const extraFeeIncome =
      sum(paidRentals, "fuel_charge") + sum(paidRentals, "damage_charge") + sum(paidRentals, "other_fee");
    // Deposits are refundable, so they are shown but never counted as income.
    const depositsHeld = sum(paidRentals, "deposit");

    const manualIncome = entries.filter((e) => e.kind === "income" && inMonth(e.date));
    const miscIncome = sum(manualIncome, "amount");
    const totalIncome =
      rentalIncome + deliveryIncome + waiverIncome + lateFeeIncome + extraFeeIncome + miscIncome;

    const pendingRentals = rentals.filter((r) => r.status !== "cancelled" && !r.paid);
    const pendingTotal = sum(pendingRentals, "total");

    const maintInMonth = maintenance.filter((m) => inMonth(m.date));
    const maintCats = {
      Parts: sum(maintInMonth, "parts"),
      Labor: sum(maintInMonth, "labor"),
      Fuel: sum(maintInMonth, "fuel"),
      Other: sum(maintInMonth, "other"),
    };
    const maintTotal = Object.values(maintCats).reduce((a, b) => a + b, 0);

    const payrollInMonth = payroll.filter((p) => inMonth(p.date));
    const payrollTotal = sum(payrollInMonth, "amount");
    const manualExpense = entries.filter((e) => e.kind === "expense" && inMonth(e.date));
    const manualExpTotal = sum(manualExpense, "amount");
    const totalExpense = maintTotal + payrollTotal + manualExpTotal;

    const months = new Set();
    rentals.forEach((r) => months.add((r.paid_date || r.start_date || "").slice(0, 7)));
    maintenance.forEach((m) => months.add((m.date || "").slice(0, 7)));
    entries.forEach((e) => months.add((e.date || "").slice(0, 7)));
    payroll.forEach((p) => months.add((p.date || "").slice(0, 7)));
    months.add(todayStr().slice(0, 7));

    return {
      paidRentals, rentalIncome, deliveryIncome, waiverIncome, lateFeeIncome, extraFeeIncome,
      depositsHeld, manualIncome, miscIncome, totalIncome, pendingRentals, pendingTotal,
      maintInMonth, maintCats, maintTotal, payrollInMonth, payrollTotal, manualExpense,
      manualExpTotal, totalExpense, net: totalIncome - totalExpense,
      monthOptions: [...months].filter(Boolean).sort().reverse(),
    };
  }, [books.isPending, books.error, books.rentals.data, books.maintenance.data, books.entries.data, books.payroll.data, month]);

  if (books.isPending) return <div className="card"><div className="card-b"><Loading what="the books" /></div></div>;
  if (books.error) {
    return (
      <div className="card">
        <div className="card-b">
          <ErrorState error={books.error} onRetry={books.refetch} />
          <div style={{ textAlign: "center", fontSize: 12.5, color: "var(--muted)", marginTop: 8 }}>
            Books is admin-only. A staff account sees nothing here by design.
          </div>
        </div>
      </div>
    );
  }

  const fmtMonth = (ym) => {
    const [yy, mm] = ym.split("-").map(Number);
    return new Date(yy, mm - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const removeEntry = async (id) => {
    try { await delEntry.mutateAsync(id); notify("Entry deleted."); }
    catch (e) { notify(e.message); }
  };

  return (
    <>
      <div className="cal-head" style={{ marginBottom: 16 }}>
        <div className="subtabs" style={{ marginBottom: 0, borderBottom: "none", flex: 1 }}>
          {SUBS.map(([k, l]) => (
            <button
              key={k}
              className={sub === k ? "on" : ""}
              style={{ borderBottom: sub === k ? "3px solid var(--orange)" : "3px solid transparent" }}
              onClick={() => setSub(k)}
            >
              {l}
            </button>
          ))}
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ border: "1px solid var(--line)", borderRadius: 6, padding: "7px 10px" }}
        >
          <option value="all">All time</option>
          {d.monthOptions.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
        </select>
      </div>

      {sub === "overview" && (
        <>
          <div className="stat-row">
            <div className="stat ok">
              <div className="tag">Income {month === "all" ? "(all time)" : ""}</div>
              <div className="num">{fmtMoney(d.totalIncome)}</div>
              <div className="sub">collected payments</div>
            </div>
            <div className="stat warn">
              <div className="tag">Expenses</div>
              <div className="num">{fmtMoney(d.totalExpense)}</div>
              <div className="sub">maintenance + payroll + other</div>
            </div>
            <div className={"stat" + (d.net >= 0 ? " money" : " warn")}>
              <div className="tag">Net</div>
              <div className="num">{fmtMoney(d.net)}</div>
              <div className="sub">income minus expenses</div>
            </div>
            <div className="stat">
              <div className="tag">Pending (uncollected)</div>
              <div className="num">{fmtMoney(d.pendingTotal)}</div>
              <div className="sub">
                {d.pendingRentals.length} unpaid rental{d.pendingRentals.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="stat">
              <div className="tag">Deposits held</div>
              <div className="num">{fmtMoney(d.depositsHeld)}</div>
              <div className="sub">refundable — not counted as income</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(310px,1fr))", gap: 18 }}>
            <div className="card">
              <div className="card-h"><h2>Income breakdown</h2></div>
              <div className="card-b tight">
                <table className="tbl"><tbody>
                  <Row label="Rentals — PAID" amount={d.rentalIncome} sub={`${d.paidRentals.length} paid rental(s)`} chip={<Chip kind="paid">Auto</Chip>} />
                  <Row label="Pickup & deliveries — PAID" amount={d.deliveryIncome} chip={<Chip kind="paid">Auto</Chip>} />
                  <Row label="Damage waivers — PAID" amount={d.waiverIncome} chip={<Chip kind="paid">Auto</Chip>} />
                  <Row label="Late fees — PAID" amount={d.lateFeeIncome} chip={<Chip kind="paid">Auto</Chip>} />
                  <Row label="Fuel / damage / other fees — PAID" amount={d.extraFeeIncome} chip={<Chip kind="paid">Auto</Chip>} />
                  <Row label="Deposits — PAID (held)" amount={d.depositsHeld} sub="Refundable, tracked separately" chip={<Chip kind="partial">Held</Chip>} />
                  <Row label="Misc & other income" amount={d.miscIncome} sub={`${d.manualIncome.length} manual entr${d.manualIncome.length === 1 ? "y" : "ies"}`} chip={<Chip kind="reserved">Manual</Chip>} />
                </tbody></table>
              </div>
            </div>

            <div className="card">
              <div className="card-h"><h2>Expense breakdown</h2></div>
              <div className="card-b tight">
                <table className="tbl"><tbody>
                  <Row label="Maintenance — Parts" amount={d.maintCats.Parts} chip={<Chip kind="overdue">Auto</Chip>} />
                  <Row label="Maintenance — Labor" amount={d.maintCats.Labor} chip={<Chip kind="overdue">Auto</Chip>} />
                  <Row label="Maintenance — Fuel" amount={d.maintCats.Fuel} chip={<Chip kind="overdue">Auto</Chip>} />
                  <Row label="Maintenance — Other" amount={d.maintCats.Other} chip={<Chip kind="overdue">Auto</Chip>} />
                  <Row label="Payroll" amount={d.payrollTotal} sub={`${d.payrollInMonth.length} payment(s)`} chip={<Chip kind="overdue">Auto</Chip>} />
                  <Row label="Other expenses" amount={d.manualExpTotal} sub={`${d.manualExpense.length} manual entr${d.manualExpense.length === 1 ? "y" : "ies"}`} chip={<Chip kind="reserved">Manual</Chip>} />
                </tbody></table>
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h2>Pending — expected in</h2>
                <div className="spacer" />
                <button className="btn sm ghost" onClick={() => onGoToTab?.("rentals")}>Rentals →</button>
              </div>
              <div className="card-b tight">
                {d.pendingRentals.length === 0 ? (
                  <div className="empty" style={{ padding: 20 }}>Nothing outstanding.</div>
                ) : (
                  <table className="tbl"><tbody>
                    {d.pendingRentals.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="main-cell">{r.customers?.name ?? "—"}</div>
                          <div className="sub-cell">{r.equipment?.name ?? "—"} · due {fmtDate(r.end_date)}</div>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(r.total)}</td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </div>
            </div>
          </div>

          <div className="notice">
            <b>Bank &amp; card linking (later):</b> importing transactions from your business cards
            needs a bank-data service such as Plaid, which is explicitly out of scope for this pass.
            For now, log card spending under Expenses.
          </div>
        </>
      )}

      {sub === "income" && (
        <div className="card">
          <div className="card-h">
            <h2>Income entries</h2>
            <div className="spacer" />
            <button className="btn primary" onClick={() => setEntryModal({ kind: "income" })}>
              <Plus size={16} /> Add income
            </button>
          </div>
          <div className="card-b tight" style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr><th>Date</th><th>Source</th><th>Category</th><th>Notes</th><th style={{ textAlign: "right" }}>Amount</th><th></th></tr>
              </thead>
              <tbody>
                {d.paidRentals.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.paid_date || r.start_date)}</td>
                    <td>
                      <div className="main-cell">{r.customers?.name ?? "—"}</div>
                      <div className="sub-cell">{r.equipment?.name ?? "—"} · {r.invoice_no}</div>
                    </td>
                    <td><Chip kind="paid">Rental payment</Chip></td>
                    <td className="sub-cell">
                      Rental {fmtMoney(r.rental_charge)}
                      {r.delivery_fee > 0 ? ` + delivery ${fmtMoney(r.delivery_fee)}` : ""}
                      {r.damage_waiver > 0 ? ` + waiver ${fmtMoney(r.damage_waiver)}` : ""}
                      {r.deposit > 0 ? ` (+${fmtMoney(r.deposit)} deposit held)` : ""}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(r.total)}</td>
                    <td />
                  </tr>
                ))}
                {d.manualIncome.map((e) => (
                  <tr key={e.id}>
                    <td>{fmtDate(e.date)}</td>
                    <td className="main-cell">{e.source || "Manual entry"}</td>
                    <td><Chip kind="reserved">{e.category}</Chip></td>
                    <td className="sub-cell">{e.notes}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(e.amount)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="iconbtn" onClick={() => setEntryModal(e)}><Pencil size={15} /></button>
                      <ConfirmButton
                        className="iconbtn"
                        title="Delete entry"
                        style={{ color: "var(--red)", borderRadius: 5, padding: "5px 6px" }}
                        label={<Trash2 size={15} />}
                        confirmLabel="Delete?"
                        onConfirm={() => removeEntry(e.id)}
                      />
                    </td>
                  </tr>
                ))}
                {d.paidRentals.length + d.manualIncome.length === 0 && (
                  <tr><td colSpan={6}>
                    <div className="empty">
                      No income recorded {month === "all" ? "yet" : "this month"}. Rental payments
                      appear here automatically when marked paid.
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sub === "expenses" && (
        <div className="card">
          <div className="card-h">
            <h2>Expense entries</h2>
            <div className="spacer" />
            <button className="btn primary" onClick={() => setEntryModal({ kind: "expense" })}>
              <Plus size={16} /> Add expense
            </button>
          </div>
          <div className="card-b tight" style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr><th>Date</th><th>What</th><th>Category</th><th>Notes</th><th style={{ textAlign: "right" }}>Amount</th><th></th></tr>
              </thead>
              <tbody>
                {d.maintInMonth.filter((m) => Number(m.cost) > 0).map((m) => (
                  <tr key={m.id}>
                    <td>{fmtDate(m.date)}</td>
                    <td>
                      <div className="main-cell">{m.equipment?.name ?? "—"}</div>
                      <div className="sub-cell">{m.type}{m.performed_by ? ` · ${m.performed_by}` : ""}</div>
                    </td>
                    <td><Chip kind="overdue">Maintenance</Chip></td>
                    <td className="sub-cell">
                      {["parts", "labor", "fuel", "other"]
                        .filter((k) => Number(m[k]) > 0)
                        .map((k) => `${k} ${fmtMoney(m[k])}`)
                        .join(" · ") || m.notes}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(m.cost)}</td>
                    <td style={{ textAlign: "right" }}>
                      <button className="btn sm ghost" onClick={() => onGoToTab?.("maintenance")}>
                        Edit in Maintenance
                      </button>
                    </td>
                  </tr>
                ))}
                {d.payrollInMonth.map((p) => (
                  <tr key={p.id}>
                    <td>{fmtDate(p.date)}</td>
                    <td className="main-cell">{p.employees?.name ?? "—"}</td>
                    <td><Chip kind="overdue">Payroll</Chip></td>
                    <td className="sub-cell">{p.notes}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(p.amount)}</td>
                    <td style={{ textAlign: "right" }}>
                      <button className="btn sm ghost" onClick={() => setSub("payroll")}>Payroll →</button>
                    </td>
                  </tr>
                ))}
                {d.manualExpense.map((e) => (
                  <tr key={e.id}>
                    <td>{fmtDate(e.date)}</td>
                    <td className="main-cell">{e.source || "Manual entry"}</td>
                    <td><Chip kind="reserved">{e.category}</Chip></td>
                    <td className="sub-cell">{e.notes}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(e.amount)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="iconbtn" onClick={() => setEntryModal(e)}><Pencil size={15} /></button>
                      <ConfirmButton
                        className="iconbtn"
                        title="Delete entry"
                        style={{ color: "var(--red)", borderRadius: 5, padding: "5px 6px" }}
                        label={<Trash2 size={15} />}
                        confirmLabel="Delete?"
                        onConfirm={() => removeEntry(e.id)}
                      />
                    </td>
                  </tr>
                ))}
                {d.maintInMonth.filter((m) => Number(m.cost) > 0).length + d.payrollInMonth.length + d.manualExpense.length === 0 && (
                  <tr><td colSpan={6}>
                    <div className="empty">
                      No expenses recorded {month === "all" ? "yet" : "this month"}. Maintenance costs
                      and payroll flow in automatically.
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sub === "payroll" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 18 }}>
          <div className="card">
            <div className="card-h">
              <h2>Employees</h2>
              <div className="spacer" />
              <button className="btn primary sm" onClick={() => setEmpModal({})}><Plus size={15} /> Add</button>
            </div>
            <div className="card-b tight">
              {books.employees.data.length === 0 ? (
                <div className="empty" style={{ padding: 20 }}>No employees yet.</div>
              ) : (
                <table className="tbl"><tbody>
                  {books.employees.data.map((e) => (
                    <tr key={e.id} className="rowbtn" onClick={() => setEmpModal(e)}>
                      <td>
                        <div className="main-cell">{e.name}</div>
                        <div className="sub-cell">{e.role}</div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {fmtMoney(e.pay_rate)}<span className="sub-cell">/{e.pay_type === "salary" ? "yr" : "hr"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h2>Payments</h2>
              <div className="spacer" />
              <button
                className="btn primary sm"
                disabled={books.employees.data.length === 0}
                title={books.employees.data.length === 0 ? "Add an employee first" : undefined}
                onClick={() => setPayModal({})}
              >
                <Plus size={15} /> Record
              </button>
            </div>
            <div className="card-b tight">
              {books.payroll.data.length === 0 ? (
                <div className="empty" style={{ padding: 20 }}>No payroll recorded.</div>
              ) : (
                <table className="tbl"><tbody>
                  {books.payroll.data.map((p) => (
                    <tr key={p.id} className="rowbtn" onClick={() => setPayModal(p)}>
                      <td>
                        <div className="main-cell">{p.employees?.name ?? "—"}</div>
                        <div className="sub-cell">{fmtDate(p.date)}{p.notes ? ` · ${p.notes}` : ""}</div>
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(p.amount)}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </div>
          </div>
        </div>
      )}

      {sub === "loans" && (
        <div className="card">
          <div className="card-h">
            <h2>Loans &amp; financing</h2>
            <div className="spacer" />
            <button className="btn primary" onClick={() => setLoanModal({})}><Plus size={16} /> Add loan</button>
          </div>
          <div className="card-b tight" style={{ overflowX: "auto" }}>
            {books.loans.data.length === 0 ? (
              <div className="empty" style={{ padding: 26 }}>
                <b>No loans tracked</b>
                <div>Add equipment financing to keep balances and payment dates in one place.</div>
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr><th>Lender</th><th>Purpose</th><th style={{ textAlign: "right" }}>Balance</th><th style={{ textAlign: "right" }}>Payment</th><th>Rate</th><th>Due</th><th></th></tr>
                </thead>
                <tbody>
                  {books.loans.data.map((l) => (
                    <tr key={l.id} className="rowbtn" onClick={() => setLoanModal(l)}>
                      <td className="main-cell">{l.lender}</td>
                      <td className="sub-cell">{l.purpose}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(l.balance)}</td>
                      <td style={{ textAlign: "right" }}>{fmtMoney(l.payment)}</td>
                      <td>{l.rate ? `${l.rate}%` : "—"}</td>
                      <td>{l.due_day ? `day ${l.due_day}` : "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="iconbtn" onClick={(ev) => { ev.stopPropagation(); setLoanModal(l); }}>
                          <Pencil size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {entryModal && (
        <EntryForm
          rec={entryModal.id ? entryModal : null}
          kind={entryModal.kind}
          categories={books.categories.data}
          onClose={() => setEntryModal(null)}
        />
      )}
      {empModal && <EmployeeForm rec={empModal.id ? empModal : null} onClose={() => setEmpModal(null)} />}
      {payModal && (
        <PayrollForm
          rec={payModal.id ? payModal : null}
          employees={books.employees.data}
          onClose={() => setPayModal(null)}
        />
      )}
      {loanModal && <LoanForm rec={loanModal.id ? loanModal : null} onClose={() => setLoanModal(null)} />}
    </>
  );
}
